import React, { ReactElement } from 'react'
import { Flex, Text } from 'theme-ui'
import { Button } from 'ui/Buttons'
import ButtonArrow from 'images/button-arrow-right.svg'
import { Svg } from 'ui/Svg'
import Checkmark from 'images/checkmark-green.svg'
import { navigate } from 'gatsby'
import LargeBackgroundLogo from 'images/organization/homepage/sections/stopThinkingAboutDns/logo-large-background.svg'
import StarsWrapper, { bigStarStyles } from 'components/Organization/HomePage/StarsWrapper'
import AnimatedSquare from './AnimatedSquare'
import HeroUIIcon from 'images/organization/homepage/sections/stopThinkingAboutDns/hero-image-b2b.svg'
import IntegrationsSection from '../Integrations'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import { TextWithBreakpointVariant } from 'ui/TextWithBreakpointVariant'

const controlDFunctions = [
  {
    text: 'Deploy Control D in minutes on your device fleet using any RMM',
  },
  {
    text: 'Block malware, harmful content, trackers and ads in seconds',
  },
  {
    text: 'Go beyond blocking with privacy features',
  },
]

export default function StopThinkingAboutDns(): ReactElement {
  const isMobile = useBreakpointIndex() === 0
  const isDesktop = useBreakpointIndex() === 2

  return (
    <Flex
      data-testid="stop-thinking-about-dns-section"
      className="ios-scrolling-glitches-fix"
      sx={{
        width: '100%',
        flexDirection: 'column',
        backgroundColor: 'blue800',
        pt: ['0', '8rem', '12.5rem'],
        px: ['0', '7rem', 0],
        position: 'relative',
        pb: ['6.4rem', '12rem', '10rem'],
        borderBottom: ['1px solid', 'none'],
        borderColor: 'blueYonder20',
        '@media screen and (min-width: 768px) and (max-width: 980px)': {
          gap: 0,
        },
      }}
    >
      {isDesktop && (
        <Svg
          svg={LargeBackgroundLogo}
          descriptiveText="Decorative background logo"
          sx={{
            position: 'absolute',
            zIndex: 'zIndex0',
            top: '2.5rem',
          }}
        />
      )}

      <StarsWrapper
        sx={{
          alignSelf: 'center',
        }}
        starsSx={[
          {
            top: '-4rem',
            left: '6.5rem',
            ...bigStarStyles,
          },
          {
            top: '18.5rem',
            left: '-1rem',
            width: '1.2rem',
            height: '1.2rem',
          },
          {
            left: '73rem',
            top: '30rem',
            'g > circle': {
              fill: 'mauve',
            },
          },
          {
            right: '25rem',
            top: '-7.8rem',
          },
          {
            ...bigStarStyles,
            right: 0,
            bottom: '15rem',
          },
          {
            right: '28rem',
            bottom: '20rem',
          },
        ]}
      >
        <Flex sx={{ flexDirection: 'column' }}>
          <Flex
            sx={{
              flexDirection: ['column', 'row'],
              justifyContent: 'center',
              px: [0, 0, '11rem'],
              gap: ['none', '4.8rem', '7.6rem'],
              borderBottom: ['1px solid', 'none'],
              borderColor: 'blueYonder20',
              zIndex: 'zIndex1',
              '@media screen and (min-width: 768px) and (max-width: 980px)': {
                gap: '2rem',
              },
              '@media (min-width: 1120px) and (max-width: 1230px)': {
                gap: '0.8rem',
              },
            }}
          >
            <Flex
              sx={{
                width: ['100%', '43.2rem', '56.8rem'],
                height: ['100%', '50.9rem', '56.7rem'],
                mt: ['6.4rem', 0, '5.4rem'],
                flexDirection: 'column',
                px: ['1.6rem', 0],
                alignContent: 'center',
                '@media screen and (min-width: 768px) and (max-width: 980px)': {
                  width: '35rem',
                },
              }}
            >
              <Flex
                sx={{
                  maxWidth: ['100%', '65rem'],
                  gap: ['2.4rem', '1.6rem', '2.4rem'],
                  flexDirection: 'column',
                  textAlign: ['center', 'inherit'],
                  justifyContent: 'center',
                }}
              >
                <TextWithBreakpointVariant
                  variant={['size32Weight500', 'size42Weight500']}
                  sx={{
                    display: 'inline-block',
                    background:
                      'linear-gradient(94.47deg, #E9EFFF 0%, rgba(198, 215, 255, 0.5) 108.33%)',
                    backgroundClip: 'text',
                    color: 'transparent',
                  }}
                  as="h1"
                >
                  Secure, Filter, and Control Your Network
                </TextWithBreakpointVariant>
                <TextWithBreakpointVariant
                  variant={['size14Weight500Line138Letter056', 'size16Weight500Line138Letter032']}
                  sx={{ color: 'wildBlueYonder', display: 'inline-block' }}
                >
                  Control D is a modern and customizable DNS service that blocks threats, unwanted
                  content and ads - on all devices. Onboard in minutes, and forget about it.
                </TextWithBreakpointVariant>
                {/* isMobile is important for the performance score */}
                {isMobile && (
                  <AnimatedSquare>
                    <Svg
                      svg={HeroUIIcon}
                      descriptiveText="Control D interface illustration for mobile"
                      sx={{
                        height: '75%',
                        WebkitTransform: 'translateZ(0)',
                      }}
                    />
                  </AnimatedSquare>
                )}
                <Flex
                  sx={{
                    gap: '1.6rem',
                    alignContent: ['center', 'flex-start'],
                    flexDirection: ['column', 'row', 'row'],
                    '@media screen and (min-width: 768px) and (max-width: 980px)': {
                      width: '20rem',
                      flexWrap: 'wrap',
                    },
                  }}
                >
                  <Button
                    data-testid="take-it-now-button"
                    sx={{ whiteSpace: 'nowrap', minWidth: ['none', '24rem', 'none'] }}
                    variant="gradientButton"
                    ariaLabel="Start Free Trial"
                    name="Start 30 Day Free Trial"
                    onClick={() => navigate('/onboard')}
                  >
                    <Text>Start 30 Day Free Trial</Text>
                    <Svg
                      svg={ButtonArrow}
                      descriptiveText="Start free trial button arrow"
                      sx={{
                        ml: '0.6rem',
                      }}
                    />
                  </Button>
                  <Button
                    ariaLabel="Book a Demo"
                    name="Book a Demo"
                    data-testid="request-demo-button"
                    sx={{
                      whiteSpace: 'nowrap',
                      '@media screen and (min-width: 768px) and (max-width: 980px)': {
                        width: '100%',
                        minWidth: ['none', '24rem', 'none'],
                      },
                    }}
                    variant="roundedSecondary"
                    onClick={() => navigate('/demo')}
                  >
                    <Text>Book a Demo</Text>
                  </Button>
                </Flex>
                <Flex
                  sx={{
                    pt: ['2.4rem', '2.4rem', '7.6rem'],
                    pb: ['6.4rem', 'none'],
                    gap: '2.4rem',
                    flexDirection: 'column',
                    '@media screen and (min-width: 1121px) and (max-width: 1500px)': {
                      pt: '2rem',
                    },
                  }}
                >
                  {controlDFunctions.map((item, index) => (
                    <Flex
                      key={index}
                      sx={{
                        gap: '1.2rem',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        color: 'lightSteelBlue',
                      }}
                    >
                      <Svg
                        sx={{
                          flexShrink: 0,
                        }}
                        svg={Checkmark}
                        descriptiveText={`Feature checkmark: ${item.text}`}
                      />
                      <TextWithBreakpointVariant
                        variant={['size14Weight400', 'size16Weight400']}
                        sx={{
                          textAlign: 'left',
                        }}
                      >
                        {item.text}
                      </TextWithBreakpointVariant>
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            </Flex>

            {/* !isMobile is important for the performance score */}
            {!isMobile && (
              <Flex
                sx={{
                  position: 'relative',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AnimatedSquare>
                  <Svg
                    svg={HeroUIIcon}
                    descriptiveText="Control D interface illustration"
                    sx={{ height: '75%' }}
                  />
                </AnimatedSquare>
                <Flex
                  sx={{
                    position: 'absolute',
                    display: ['none', 'none', 'flex'],
                    zIndex: 'zIndex0',
                    mt: '5.2rem',
                    right: '45%',
                    width: '70%',
                    height: '49.5rem',
                    flexShrink: '0',
                    borderRadius: '49.5rem',
                    opacity: 0.4,
                    background: 'steelPink',
                    filter: 'blur(125px)',
                  }}
                />
              </Flex>
            )}
          </Flex>

          <IntegrationsSection
            sx={{
              pt: ['6.4rem', '18.5rem'],
            }}
          />
        </Flex>
      </StarsWrapper>
    </Flex>
  )
}
